import React, { useEffect } from 'react';
import { updateDoc, serverTimestamp } from 'firebase/firestore';
import { Save, Edit, Delete, CameraAlt, Numbers, Accessibility, Checkroom, IceSkating, LocationOn, Lightbulb, Psychology, Person, RemoveRedEye, Cake, Fingerprint, ContentCut, Mood, Settings } from '@mui/icons-material';
import PromptAutomatorPromptTemplate from './PromptAutomatorPromptTemplate';
import './PromptAutomatorRightPanel.css';

const promptFields = [
  { id: 'promptTemplate', label: 'Template', icon: Settings },
  { id: 'photography', label: 'Photography', icon: CameraAlt },
  { id: 'quantity', label: 'Quantity', icon: Numbers },
  { id: 'pose', label: 'Pose', icon: Accessibility },
  { id: 'personalityTemplate', label: 'Personality Template', icon: Psychology },
  { id: 'outfit', label: 'Outfit', icon: Checkroom },
  { id: 'shoes', label: 'Shoes', icon: IceSkating },
  { id: 'location', label: 'Location', icon: LocationOn },
  { id: 'light', label: 'Light', icon: Lightbulb },
  { id: 'eyes', label: 'Eyes', icon: RemoveRedEye },
  { id: 'age', label: 'Age', icon: Cake },
  { id: 'skin', label: 'Skin', icon: Fingerprint },
  { id: 'hairs', label: 'Hairs', icon: ContentCut },
  { id: 'mood', label: 'Mood', icon: Mood },
  { id: 'personality', label: 'Personality', icon: Psychology },
];

function PromptAutomatorRightPanel({ 
  selectedTask,
  selectedPromptType,
  promptValues,
  setPromptValues,
  currentPromptValue,
  setCurrentPromptValue,
  saving,
  setSaving,
  editingPromptId,
  setEditingPromptId,
  editingPromptValue,
  setEditingPromptValue,
  deleteModalOpen,
  setDeleteModalOpen,
  promptToDelete,
  setPromptToDelete,
  setTasks,
  setSelectedTask,
  setNotification,
  user,
  getAgencyDoc
}) {

  useEffect(() => {
    if (selectedPromptType) {
      if ((selectedPromptType.id === 'promptTemplate' || selectedPromptType.id === 'personalityTemplate') && promptValues[selectedPromptType.id]) {
        setCurrentPromptValue(promptValues[selectedPromptType.id]);
      } else {
        setCurrentPromptValue('');
      }
    } else {
      setCurrentPromptValue('');
    }
  }, [selectedPromptType, promptValues, setCurrentPromptValue]);

  const handleAddPrompt = async () => {
    if (!selectedTask || !selectedPromptType || !currentPromptValue.trim() || !user) return;

    try {
      setSaving(true);
      
      let newPromptValues = { ...promptValues };
      
      if (selectedPromptType.id === 'promptTemplate' || selectedPromptType.id === 'personalityTemplate') {
        newPromptValues[selectedPromptType.id] = currentPromptValue.trim();
      } else {
        if (!newPromptValues[selectedPromptType.id]) {
          newPromptValues[selectedPromptType.id] = [];
        }
        newPromptValues[selectedPromptType.id] = [
          ...newPromptValues[selectedPromptType.id],
          {
            id: Date.now().toString(),
            value: currentPromptValue.trim(),
            createdAt: new Date().toISOString(),
            weight: 1
          }
        ];
      }
      
      const taskRef = getAgencyDoc('aitask', selectedTask.id);
      if (!taskRef) return;
      
      await updateDoc(taskRef, { 
        prompts: newPromptValues,
        lastModified: serverTimestamp(),
        lastModifiedBy: user.email
      });
      
      setPromptValues(newPromptValues);
      setSelectedTask(prev => ({ ...prev, prompts: newPromptValues }));
      setTasks(prev => prev.map(task => 
        task.id === selectedTask.id 
          ? { ...task, prompts: newPromptValues }
          : task
      ));
      
      setCurrentPromptValue('');
      setNotification('Prompt saved successfully');
      
    } catch (error) {
      console.error('Error saving prompt:', error);
      setNotification('❌ Failed to save prompt');
      setTimeout(() => setNotification(''), 3000);
    } finally {
      setSaving(false);
      setTimeout(() => setNotification(''), 3000);
    }
  };

  const handleEditPrompt = (promptId, currentValue) => {
    setEditingPromptId(promptId);
    setEditingPromptValue(currentValue);
  };

  const handleCancelEdit = () => {
    setEditingPromptId(null);
    setEditingPromptValue('');
  };

  const handleSaveEdit = async () => {
    if (!selectedTask || !selectedPromptType || !user || !editingPromptId || !editingPromptValue.trim()) return;

    try {
      setSaving(true);
      let newPromptValues = { ...promptValues };
      
      if (selectedPromptType.id === 'promptTemplate' || selectedPromptType.id === 'personalityTemplate') {
        newPromptValues[selectedPromptType.id] = editingPromptValue.trim();
      } else {
        if (newPromptValues[selectedPromptType.id]) {
          newPromptValues[selectedPromptType.id] = newPromptValues[selectedPromptType.id].map(prompt => 
            prompt.id === editingPromptId 
              ? { ...prompt, value: editingPromptValue.trim() }
              : prompt
          );
        }
      }
      
      const taskRef = getAgencyDoc('aitask', selectedTask.id);
      if (!taskRef) return;
      
      await updateDoc(taskRef, { 
        prompts: newPromptValues,
        lastModified: serverTimestamp(),
        lastModifiedBy: user.email
      });
      
      setPromptValues(newPromptValues);
      setSelectedTask(prev => ({ ...prev, prompts: newPromptValues }));
      setTasks(prev => prev.map(task => 
        task.id === selectedTask.id 
          ? { ...task, prompts: newPromptValues }
          : task
      ));
      
      handleCancelEdit();
      setNotification('✅ Prompt updated successfully');
      
    } catch (error) {
      console.error('Error updating prompt:', error);
      setNotification('❌ Failed to update prompt');
    } finally {
      setSaving(false);
      setTimeout(() => setNotification(''), 3000);
    }
  };

  const handleWeightChange = async (promptId) => {
    if (!selectedTask || !selectedPromptType || !user) return;

    try {
      let newPromptValues = { ...promptValues };
      
      if (newPromptValues[selectedPromptType.id]) {
        newPromptValues[selectedPromptType.id] = newPromptValues[selectedPromptType.id].map(prompt => {
          if (prompt.id === promptId) {
            const currentWeight = prompt.weight || 1;
            let newWeight;
            
            switch (currentWeight) {
              case 1: newWeight = 2; break;
              case 2: newWeight = 4; break;
              case 4: newWeight = 8; break;
              case 8: newWeight = 1; break;
              default: newWeight = 1;
            }
            
            return { ...prompt, weight: newWeight };
          }
          return prompt;
        });
      }
      
      const taskRef = getAgencyDoc('aitask', selectedTask.id);
      if (!taskRef) return;
      
      await updateDoc(taskRef, { 
        prompts: newPromptValues,
        lastModified: serverTimestamp(),
        lastModifiedBy: user.email
      });
      
      setPromptValues(newPromptValues);
      setSelectedTask(prev => ({ ...prev, prompts: newPromptValues }));
      setTasks(prev => prev.map(task => 
        task.id === selectedTask.id 
          ? { ...task, prompts: newPromptValues }
          : task
      ));
      
    } catch (error) {
      console.error('Error changing weight:', error);
    }
  };

  const openDeleteModal = (promptId, promptValue) => {
    setPromptToDelete({ id: promptId, value: promptValue });
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!promptToDelete) return;
    
    try {
      const updatedPromptValues = { ...promptValues };
      if (selectedPromptType.id === 'promptTemplate' || selectedPromptType.id === 'personalityTemplate') {
        updatedPromptValues[selectedPromptType.id] = '';
      } else {
        const prompts = updatedPromptValues[selectedPromptType.id] || [];
        updatedPromptValues[selectedPromptType.id] = prompts.filter(p => p.id !== promptToDelete.id);
      }

      const taskRef = getAgencyDoc('aitask', selectedTask.id);
      if (!taskRef) return;

      await updateDoc(taskRef, {
        prompts: updatedPromptValues,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });

      setPromptValues(updatedPromptValues);
      setSelectedTask(prev => ({ ...prev, prompts: updatedPromptValues }));
      setTasks(prev => prev.map(task => 
        task.id === selectedTask.id 
          ? { ...task, prompts: updatedPromptValues }
          : task
      ));
      setPromptToDelete(null);
      setDeleteModalOpen(false);
      setNotification('🗑️ Prompt deleted successfully');
      setTimeout(() => setNotification(''), 3000);
    } catch (error) {
      console.error('Error deleting prompt:', error);
      setNotification('❌ Failed to delete prompt');
      setTimeout(() => setNotification(''), 3000);
    }
  };

  const getCurrentPrompts = () => {
    if (!selectedPromptType || !promptValues[selectedPromptType.id]) return [];
    
    if (selectedPromptType.id === 'promptTemplate' || selectedPromptType.id === 'personalityTemplate') {
      return [{
        id: selectedPromptType.id,
        value: promptValues[selectedPromptType.id]
      }];
    } else {
      const prompts = promptValues[selectedPromptType.id];
      return Array.isArray(prompts) ? prompts.slice().reverse() : [];
    }
  };

  const currentPrompts = getCurrentPrompts();

  if (!selectedPromptType) {
    return (
      <div className="right-panel">
        <div className="right-panel__empty">
          <div className="empty-icon">✨</div>
          <h3 className="empty-title">Select Prompt Type</h3>
          <p className="empty-text">Choose a prompt type from the center panel to start editing</p>
        </div>
      </div>
    );
  }

  return (
    <div className="right-panel">
      <div className="right-panel__input">
        {(selectedPromptType.id === 'promptTemplate' || selectedPromptType.id === 'personalityTemplate') ? (
          <PromptAutomatorPromptTemplate
            value={currentPromptValue}
            onChange={setCurrentPromptValue}
            availableTags={
              selectedPromptType.id === 'personalityTemplate' 
                ? promptFields.filter(field => ['eyes', 'age', 'skin', 'hairs', 'mood'].includes(field.id))
                : promptFields.filter(field => !['promptTemplate', 'personalityTemplate', 'eyes', 'age', 'skin', 'hairs', 'mood'].includes(field.id))
            }
            onSave={handleAddPrompt}
            saving={saving}
          />
        ) : (
          <div className="prompt-input-zone">
            <div className="textarea-with-button">
              <textarea
                value={currentPromptValue}
                onChange={(e) => setCurrentPromptValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (currentPromptValue.trim() && !saving) {
                      handleAddPrompt();
                    }
                  }
                }}
                placeholder={`Prompt something...`}
                className="prompt-textarea"
                spellCheck={false}
                rows={3}
              />
              <button 
                onClick={handleAddPrompt}
                disabled={saving || !currentPromptValue.trim()}
                className="save-btn-circular"
                title={saving ? 'Saving...' : 'Save Prompt'}
              >
                {React.createElement(Save)}
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedPromptType.id !== 'promptTemplate' && selectedPromptType.id !== 'personalityTemplate' && currentPrompts && currentPrompts.length > 0 && (
        <div className="right-panel__saved">
          <div className="saved-list">
              {currentPrompts.map((prompt, index) => (
                <div key={prompt.id || index} className="saved-item">
                  {editingPromptId === prompt.id ? (
                    <div className="edit-section">
                      <textarea
                        value={editingPromptValue}
                        onChange={(e) => setEditingPromptValue(e.target.value)}
                        className="edit-textarea"
                        rows={3}
                        autoFocus
                      />
                      <div className="edit-actions">
                        <button 
                          onClick={handleSaveEdit}
                          disabled={saving || !editingPromptValue.trim()}
                          className="edit-btn edit-btn--save"
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button 
                          onClick={handleCancelEdit}
                          className="edit-btn edit-btn--cancel"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="item-content">
                      <div className="item-text">{prompt.value}</div>
                      <div className="item-actions">
                        <button 
                          onClick={() => openDeleteModal(prompt.id, prompt.value)}
                          className="item-actions__btn item-actions__btn--delete"
                          title="Delete prompt"
                        >
                          ×
                        </button>
                        <button 
                          onClick={() => handleEditPrompt(prompt.id, prompt.value)}
                          className="item-actions__btn item-actions__btn--edit"
                          title="Edit prompt"
                        >
                          <Edit style={{ fontSize: '14px' }} />
                        </button>
                        <button 
                          onClick={() => handleWeightChange(prompt.id)}
                          className="item-actions__weight"
                          data-weight={prompt.weight || 1}
                          title={`Current weight: x${prompt.weight || 1}. Click to change.`}
                        >
                          x{prompt.weight || 1}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="modal__overlay" onClick={() => setDeleteModalOpen(false)}>
          <div className="modal__content" onClick={(e) => e.stopPropagation()}>
            <div className="modal__header">
              <h3 className="modal__title">
                <Delete />
                Delete Prompt
              </h3>
            </div>
            <div className="modal__body">
              <p>
                Are you sure you want to delete this prompt? This action cannot be undone.
              </p>
            </div>
            <div className="modal__actions">
              <button 
                onClick={() => setDeleteModalOpen(false)}
                className="modal__button modal__button--cancel"
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete}
                className="modal__button modal__button--delete"
              >
                Delete Prompt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromptAutomatorRightPanel;